import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import first from 'lodash.first';
import { PromiseState } from 'react-refetch';

import connect from 'helpers/apiConnect';
import { loadCountries } from 'redux/reducers/countries';

import getChartPositionsUrl from 'url-constructors/getChartPositionsUrl';

import useMedia from 'hooks/useMedia';
import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';

import OverviewContainer from 'components/Overview/Container/Container';
import DSPChartsTable from 'components/data-containers/Overview/DSPChartPerformance/DSPChartsTable';
import useChartPositions from 'components/data-containers/PositionsTable/shared/hooks/useChartPositions';
import useFilteredCharts from 'components/data-containers/PositionsTable/shared/hooks/useFilteredCharts';
import useGlobalChartRows from 'components/data-containers/PositionsTable/shared/hooks/useGlobalChartRows';
import { EntityTypes } from 'context/entityContext';
import { RootState } from 'redux/rootReducer';
import { IChartItem, IDSPChartPerformanceTerritory } from './types';
import { ITerritoryDataItem } from './../types';

interface IEntity {
  id: string;
  type: EntityTypes;
  totalChartsCount?: number;
}

interface IDataSourcesProps {
  entityIds: string[];
  entityType: EntityTypes;
  compareMode: string;
  countryIds: string[];
  filters: any;
}

interface IDataSources {
  dailyChartPositions: string;
  weeklyChartPositions: string;
}

const dataSources = (props: IDataSourcesProps): IDataSources => {
  const dailyUrl = getChartPositionsUrl({
    ...props,
    chartFrequency: 'daily',
    chartType: 'dsp',
    limit: 10,
  });
  const weeklyUrl = getChartPositionsUrl({
    ...props,
    chartFrequency: 'weekly',
    chartType: 'dsp',
    limit: 10,
  });

  return {
    dailyChartPositions: dailyUrl,
    weeklyChartPositions: weeklyUrl,
  };
};

interface IChartDataItem {
  chartDisplayName: string;
  chartId: number;
  chartName: string;
  chartType: string;
  customerId: number;
  customerName: string;
  productDisplayName: string;
  productId: string;
  productType: string;
  territoryData: ITerritoryDataItem[];
  totalChartStreams: number;
  totalNumberOfCountries: number;
}

interface IDSPDailyChartPositions {
  charts: IChartDataItem[];
  entity: IEntity;
}

interface IDSPWeeklyChartPositions {
  entityData: {
    charts: IChartDataItem[];
    entity: IEntity;
  };
}

interface IDSPChartPerformance {
  dailyChartPositions: PromiseState<IDSPDailyChartPositions[]>;
  weeklyChartPositions: PromiseState<IDSPWeeklyChartPositions[]>;
  entityType: EntityTypes;
  filters: any;
  productType: string;
}

/**
 * Renders the total number of chart positions and the global and local counts separately.
 * Also renders a table of of the top 5 chart position details divided into a global and local section.
 * are shown. Renders a `No data available` placeholder in the absence of data.
 */
const DSPChartPerformance: React.FC<IDSPChartPerformance> = (props) => {
  const {
    dailyChartPositions,
    weeklyChartPositions,
    entityType,
    filters,
    productType,
  } = props;

  const [, isPending] = useCombinedRefetchState([
    dailyChartPositions,
    weeklyChartPositions,
  ]);

  const isLargeScreen = useMedia('(min-width: 60em)');
  const showProductColumn = entityType === 'artist' || entityType === 'project';

  // Load the country metadata on mount.
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCountries());
  }, [dispatch]);

  const countries = useSelector((state: RootState) => state.countries.data);

  const charts = useChartPositions(
    dailyChartPositions,
    weeklyChartPositions,
    countries,
    'currentPosition'
  );

  const filteredCharts = useFilteredCharts(charts, filters);

  const globalChartRows = useGlobalChartRows(filteredCharts, 5);

  /**
   * This memo filters the charts to only get the local ones
   * @returns {Array} charts with only local data
   */
  const localChartRows = useMemo(() => {
    if (filteredCharts) {
      return filteredCharts
        .filter(filterByLocal)
        .map(removeGlobalCharts)
        .sort(sortByStreams)
        .slice(0, 5);
    }

    return null;

    function isLocal(territory: IDSPChartPerformanceTerritory): boolean {
      return territory.countryId !== 'WW';
    }

    function filterByLocal(chart: IChartItem): boolean {
      return chart.territoryData.some(isLocal);
    }

    function removeGlobalCharts(chart: IChartItem): IChartItem {
      // during iteration assignments mutate the original array
      const copy = Object.assign({}, chart);
      copy.territoryData = chart.territoryData.filter(isLocal);
      return copy;
    }

    /*
     * sort the given array by currentPosition, when positions are equal
     * the one with the most streams gets sorted on top
     */
    function sortByStreams(left: IChartItem, right: IChartItem): number {
      if (left.currentPosition === right.currentPosition) {
        const leftTerritory = first(left.territoryData);
        const rightTerritory = first(right.territoryData);
        if (leftTerritory?.chartStreams && rightTerritory?.chartStreams) {
          return leftTerritory?.chartStreams - rightTerritory?.chartStreams;
        }
      }
      return left.currentPosition - right.currentPosition;
    }
  }, [filteredCharts]);

  const renderedLocalRows = localChartRows
    ? localChartRows.slice(0, globalChartRows ? 5 - globalChartRows.length : 5)
    : null;

  return (
    <OverviewContainer
      filtersToShow={['country']}
      title="DSP Charts"
      linkTo={useUrlForTopic('charts/dsp')}
    >
      <DSPChartsTable
        loading={isPending}
        globalCharts={globalChartRows || []}
        charts={renderedLocalRows || []}
        isSmallScreen={!isLargeScreen}
        productType={productType}
        showProductColumn={showProductColumn}
      />
    </OverviewContainer>
  );
};

export default connect(dataSources as any)(DSPChartPerformance as any) as any;
